import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { applyTableState, useTableState } from '@/components/blocks/data-table'
import { type SelectOption } from '@/components/ui/Select'
import { useToast } from '@/components/ui/Toast'
import { PageHeader } from '@/components/shared'
import {
  ASSET_BUNDLE_COLUMNS,
  AssetBundlesTable,
  AssetBundleUploadCard,
  CONFIG_COLUMNS,
  ConfigFilesTable,
  ConfigUploadCard,
  ReleaseDialog,
  VersionPolicyCard,
  VersionSummaryHeader,
  VersionToolbar,
  type ReleaseValues,
} from '@/components/versions'
import type { ConfigFile, GameVersion, Platform, ReleaseEvent, VersionFlags } from '@/models'

/* Module scope so the reference is stable across renders. */
const TABLE_INITIAL = { pageSize: 8 }

/* camelCase flag key -> the snake_case column the controller permits. */
const FLAG_TO_PARAM: Record<keyof VersionFlags, string> = {
  maintain: 'maintain',
  forceUpdate: 'force_update',
  fallbackNetConfig: 'fallback_net_config',
}

interface VersionsPageProps {
  versions: GameVersion[]
  canEdit: boolean
}

export function VersionsPage({ versions, canEdit }: VersionsPageProps) {
  const { toast } = useToast()
  const [platform, setPlatform] = useState<Platform>('ios')
  const [versionId, setVersionId] = useState<string>('')
  const [refreshing, setRefreshing] = useState(false)
  const [releaseOpen, setReleaseOpen] = useState(false)
  const [releaseErrors, setReleaseErrors] = useState<Record<string, string>>({})

  const { state, setState } = useTableState(TABLE_INITIAL)
  const { state: assetBundleState, setState: setAssetBundleState } = useTableState(TABLE_INITIAL)

  const platformVersions = useMemo(
    () => versions.filter((version) => version.platform === platform),
    [versions, platform],
  )

  // Highest existing version per platform, to hint the next release number.
  const latestByPlatform = useMemo(() => {
    const cmp = (a: string, b: string) => {
      const pa = a.split('.').map(Number)
      const pb = b.split('.').map(Number)
      for (let i = 0; i < 3; i += 1) {
        if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0)
      }
      return 0
    }
    const out: Partial<Record<Platform, string>> = {}
    for (const version of versions) {
      const current = out[version.platform]
      if (!current || cmp(version.version, current) > 0) out[version.platform] = version.version
    }
    return out
  }, [versions])

  const selected =
    platformVersions.find((version) => version.id === versionId) ?? platformVersions[0]

  // Switching platform strands the selected id on the other platform's list.
  // Adjusted during render rather than in an effect: it converges immediately
  // and saves a wasted commit with the stale id.
  if (selected && selected.id !== versionId) setVersionId(selected.id)

  const versionOptions: SelectOption<string>[] = platformVersions.map((version) => ({
    value: version.id,
    label: `${version.platform === 'ios' ? 'iOS' : 'Android'} version: ${version.version}`,
  }))

  const table = useMemo(
    () => applyTableState(selected?.configFiles ?? [], CONFIG_COLUMNS, state),
    [selected, state],
  )

  const assetBundleTable = useMemo(
    () => applyTableState(selected?.assetBundles ?? [], ASSET_BUNDLE_COLUMNS, assetBundleState),
    [selected, assetBundleState],
  )

  const handleFlagChange = (key: keyof VersionFlags, value: boolean) => {
    if (!selected) return
    if (!canEdit) {
      toast('You do not have permission to change release policy.', 'danger')
      return
    }

    router.patch(
      `/versions/${selected.id}`,
      { game_version: { [FLAG_TO_PARAM[key]]: value } },
      {
        preserveScroll: true,
        onError: () => toast('Could not update flag', 'danger'),
      },
    )
  }

  const handleTransition = (event: ReleaseEvent) => {
    if (!selected) return
    if (!canEdit) {
      toast('You do not have permission to change release status.', 'danger')
      return
    }

    router.post(
      `/versions/${selected.id}/transition`,
      { event },
      {
        preserveScroll: true,
        onError: (errors) => toast(errors.status ?? 'Could not change status', 'danger'),
      },
    )
  }

  const handleCopyChecksum = (file: ConfigFile) => {
    void navigator.clipboard?.writeText(file.checksum)
    toast(`Copied ${file.name} checksum`)
  }

  const handleDownload = (file: ConfigFile) => {
    // Fetch the original stored JSON body from the server.
    window.location.href = `/config_files/${file.id}/download`
  }

  const handleRefresh = () => {
    router.reload({
      only: ['versions'],
      onStart: () => setRefreshing(true),
      onFinish: () => setRefreshing(false),
    })
  }

  const handleNewRelease = () => {
    if (!canEdit) {
      toast('You do not have permission to create a release.', 'danger')
      return
    }
    setReleaseErrors({})
    setReleaseOpen(true)
  }

  const handleCreateRelease = (values: ReleaseValues) => {
    router.post(
      '/versions',
      { version: values.version, platform: values.platform },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast(`Created ${values.version}`, 'success')
          setReleaseOpen(false)
        },
        onError: (errors) => {
          // Keep the dialog open so the user can fix the input and retry.
          setReleaseErrors(errors as Record<string, string>)
          toast(errors.version ?? 'Could not create release', 'danger')
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Versions"
        description="Client update policy and config manifest, per platform version."
      />

      <VersionToolbar
        platform={platform}
        onPlatformChange={setPlatform}
        versionOptions={versionOptions}
        versionId={selected?.id ?? ''}
        onVersionChange={setVersionId}
        onRefresh={handleRefresh}
        onNewRelease={handleNewRelease}
        refreshing={refreshing}
      />

      {selected ? (
        /* 7 : 3 Column Split (10-column grid: 7 cols Main Panel, 3 cols Sidebar Controls) */
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-10">
          {/* Column 7 (70% width): Version Summary Header + Config Files Manifest Table */}
          <div className="space-y-4 lg:col-span-7">
            <VersionSummaryHeader
              version={selected}
              canEdit={canEdit}
              onTransition={handleTransition}
            />

            <div className="space-y-2">
              <h3 className="text-fg text-sm font-semibold">Asset Bundles</h3>
              <AssetBundlesTable
                rows={assetBundleTable.rows}
                total={assetBundleTable.total}
                state={assetBundleState}
                onStateChange={setAssetBundleState}
                onCopyChecksum={handleCopyChecksum}
                onDownload={handleDownload}
                loading={refreshing}
              />
            </div>

            <div className="space-y-2">
              <h3 className="text-fg text-sm font-semibold">Config Files</h3>
              <ConfigFilesTable
                rows={table.rows}
                total={table.total}
                state={state}
                onStateChange={setState}
                onCopyChecksum={handleCopyChecksum}
                onDownload={handleDownload}
                loading={refreshing}
              />
            </div>
          </div>

          {/* Column 3 (30% width): Policy flags + config/asset upload */}
          <div className="space-y-4 lg:col-span-3">
            <VersionPolicyCard version={selected} onFlagChange={handleFlagChange} />
            <AssetBundleUploadCard version={selected} canEdit={canEdit} />
            <ConfigUploadCard version={selected} canEdit={canEdit} />
          </div>
        </div>
      ) : (
        <p className="text-fg-subtle text-sm">No releases for this platform yet.</p>
      )}

      <ReleaseDialog
        open={releaseOpen}
        onOpenChange={(open) => {
          setReleaseOpen(open)
          if (!open) setReleaseErrors({})
        }}
        platform={platform}
        serverErrors={releaseErrors}
        latestByPlatform={latestByPlatform}
        onCreate={handleCreateRelease}
      />
    </div>
  )
}
